import { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import {
  GradientText,
  AnimatedCounter,
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  TiltCard,
  GlowCard,
  AwsLogo,
  GcpLogo,
  AzureLogo,
  PublicCTAPair,
} from '@/components/common';
import {
  Network,
  CheckCircle,
  Cloud,
  Layers,
  Globe,
  Radar,
  Activity,
  History,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import { apiClient } from '@/api/client';

// ─── Data ────────────────────────────────────────────────────────────────────

interface RealTimeStats {
  nodes: number;
  activeNodes: number;
  tunnels: number;
  uptime: number;
  detection: number;
  cloudRegions: number;
}

const capabilityPillars = [
  {
    Icon: Radar,
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-500/10',
    iconColor: 'text-cyan-500',
    title: 'Continuity Observation',
    description:
      'Sub-second OWL probes across a 118-node multi-cloud substrate compose a synchronized continuity surface that is the canonical reference for Internet behavior.',
    features: [
      'OWL 1 Hz one-way latency probes',
      '118 observation nodes across AWS / GCP / Azure',
      'Multi-vantage stitching via BorderNodes',
      'Append-only canonical observation objects',
    ],
  },
  {
    Icon: Activity,
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-500/10',
    iconColor: 'text-emerald-500',
    title: 'Anomaly Detection',
    description:
      'Deviation Signatures surface regional incidents in seconds, classified across behavioral signature types, correlated across PulseGroups, never acted upon.',
    features: [
      'Deviation Signature classification',
      'Per-corridor behavioral baselines',
      'Probabilistic continuity surface',
      'Cross-region anomaly correlation',
    ],
  },
  {
    Icon: History,
    color: 'from-violet-500 to-purple-600',
    bgColor: 'bg-violet-500/10',
    iconColor: 'text-violet-500',
    title: 'Replayable State',
    description:
      'Every observation is append-only and cryptographically chained. Full forensic replay across hot, warm, cold, and archival tiers is deterministic by design.',
    features: [
      'Append-only canonical objects',
      'Cryptographic lineage ledger',
      'Hot / warm / cold / archival tiers',
      'Deterministic replay continuity',
    ],
  },
];

// Three rows describing where DDARP sits in the network stack.
// Top row is DDARP (the observation layer). Middle row is existing
// per-vendor telemetry. Bottom row is control planes — which DDARP
// observes but never modifies (Patent Claim 11, operational neutrality).
const stackLayers = [
  {
    role: 'ddarp',
    title: 'DDARP — Externalized Observation Layer',
    subtitle:
      'Synthesizes a synchronized continuity surface across many vantage points; publishes intelligence streams; never modifies anything below.',
    items: [],
  },
  {
    role: 'telemetry',
    title: 'Existing Network Telemetry (per vendor, per silo)',
    subtitle: 'Sources DDARP composes alongside its own OWL substrate.',
    items: ['NetFlow', 'sFlow', 'BGP-LS', 'BMP', 'SNMP'],
  },
  {
    role: 'control',
    title: 'Control Planes (untouched by DDARP)',
    subtitle: 'Routing and forwarding decisions remain entirely with their operators.',
    items: ['BGP', 'OSPF', 'IS-IS', 'MPLS-TE', 'IGPs'],
  },
];

// ─── Subcomponents ──────────────────────────────────────────────────────────

/**
 * Single row in the "Where DDARP sits in the stack" visualization.
 */
function StackRow({ layer, index }: { layer: typeof stackLayers[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const isDdarp = layer.role === 'ddarp';
  const isControl = layer.role === 'control';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
      transition={{ delay: index * 0.12, duration: 0.5 }}
      className={
        'rounded-xl border p-5 md:p-6 ' +
        (isDdarp
          ? 'border-primary/30 bg-gradient-to-r from-primary-50 to-cyan-50 shadow-sm'
          : isControl
            ? 'border-slate-200 bg-slate-50/60'
            : 'border-slate-200 bg-white')
      }
    >
      <div className="flex items-start gap-3 mb-3">
        <span
          className={
            'mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0 ' +
            (isDdarp ? 'bg-primary' : isControl ? 'bg-slate-400' : 'bg-cyan-500')
          }
        />
        <div>
          <h3
            className={
              'text-base md:text-lg font-semibold ' +
              (isDdarp ? 'text-primary-700' : 'text-slate-900')
            }
          >
            {layer.title}
          </h3>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{layer.subtitle}</p>
        </div>
      </div>
      {layer.items.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 ml-5">
          {layer.items.map((item) => (
            <span
              key={item}
              className={
                'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono ' +
                (isControl
                  ? 'bg-white border border-slate-200 text-slate-600'
                  : 'bg-white border border-cyan-200 text-cyan-700')
              }
            >
              {item}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function Home() {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 0.4], [0, -60]);

  // Real-time stats from API
  const [stats, setStats] = useState<RealTimeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiClient.get('/api/public/stats');
        const data = response.data;
        if (data) {
          setStats({
            nodes: data.total_nodes,
            activeNodes: data.active_nodes,
            tunnels: data.active_tunnels,
            uptime: data.uptime_percentage ?? 100,
            detection: 3,
            cloudRegions: data.cloud_regions,
          });
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative">
      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 1: HERO — V2 thesis, Doppler-radar backdrop
          ══════════════════════════════════════════════════════════════════════ */}
      <section
        ref={heroRef}
        className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#0d063e' }}
      >
        {/* Doppler-radar Higgsfield backdrop */}
        <img
          src="/images/hero/hero-doppler-radar.webp"
          alt=""
          aria-hidden
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none"
        />

        {/* Hero Content */}
        <motion.div
          style={{ opacity: heroOpacity, y: heroY }}
          className="relative z-10 text-center px-4 max-w-5xl mx-auto"
        >
          {/* Live Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 mb-8"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
            </span>
            <span className="text-sm font-medium text-white">
              {isLoading ? 'Connecting…' : `${stats?.activeNodes ?? 0} observation nodes online`}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight"
          >
            <GradientText
              text="Internet Continuity Intelligence"
              colors={['#60A5FA', '#93C5FD', '#C4B5FD']}
              animationDuration={6}
            />
          </motion.h1>

          {/* Protocol name */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 text-white/90"
          >
            Distributed Detection And Routing Protocol
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-white/80 text-lg font-medium max-w-2xl mx-auto mb-10"
          >
            A synchronized, replayable, externalized observation layer for the
            public Internet. Doppler radar for global continuity, never a router,
            never a control plane.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <PublicCTAPair variant="dark" />
          </motion.div>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 2: LIVE METRICS — Observation Substrate
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative pb-20 pt-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Observation Substrate
              </h2>
              <p className="text-gray-900 max-w-2xl mx-auto">
                Live numbers from the globally distributed multi-cloud measurement fabric.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 mb-10">
            {[
              { value: stats?.nodes ?? 0, label: 'Observation Nodes', sub: 'deployed', icon: <Globe className="w-5 h-5" />, suffix: '' },
              { value: stats?.tunnels ?? 0, label: 'Mesh Probe Tunnels', sub: 'full mesh', icon: <Network className="w-5 h-5" />, suffix: '' },
              { value: 3, label: 'Hyperscale Clouds', sub: 'AWS · GCP · Azure', icon: <Cloud className="w-5 h-5" />, suffix: '' },
              { value: 91, label: 'Patent Claims', sub: 'USPTO provisional', icon: <FileText className="w-5 h-5" />, suffix: '' },
              { value: 751, label: 'Target Topology', sub: '118 + 65 anchor + 568 API', icon: <Layers className="w-5 h-5" />, suffix: '' },
            ].map((stat, index) => (
              <ScrollReveal key={stat.label} delay={index * 0.1}>
                <GlowCard className="p-6 text-center">
                  <div className="flex justify-center mb-4 text-primary">
                    {stat.icon}
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
                    <AnimatedCounter
                      value={stat.value}
                      suffix={stat.suffix}
                      duration={2.5}
                    />
                  </div>
                  <div className="text-gray-900 text-sm font-medium">{stat.label}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{stat.sub}</div>
                </GlowCard>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.4}>
            <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
              <span className="text-gray-900 font-medium">Powered by</span>
              <div className="flex items-center gap-3 flex-wrap justify-center">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 text-sm font-semibold shadow-sm">
                  <AwsLogo height={18} /> AWS
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 text-sm font-semibold shadow-sm">
                  <GcpLogo height={18} /> GCP
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 text-sm font-semibold shadow-sm">
                  <AzureLogo height={18} /> Azure
                </span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 3: THREE CAPABILITY PILLARS — V2 observation thesis
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-50 border border-primary/10 text-primary text-sm font-medium mb-4">
                <Layers className="w-4 h-4" />
                Core Capabilities
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Three Pillars of Continuity Intelligence
              </h2>
              <p className="text-gray-900 max-w-2xl mx-auto">
                Observation, detection, and replay, composed across a 118-node
                substrate into a canonical reference for Internet behavior.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilityPillars.map((pillar) => {
              const Icon = pillar.Icon;
              return (
                <StaggerItem key={pillar.title}>
                  <TiltCard className="h-full" maxTilt={6} glare glareMaxOpacity={0.15}>
                    <div className="p-8 h-full bg-white rounded-2xl border border-gray-100 shadow-sm">
                      {/* Icon */}
                      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${pillar.bgColor} ${pillar.iconColor} mb-6`}>
                        <Icon className="w-7 h-7" />
                      </div>

                      {/* Title & Description */}
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{pillar.title}</h3>
                      <p className="text-gray-900 text-sm mb-6 leading-relaxed">{pillar.description}</p>

                      {/* Feature List */}
                      <ul className="space-y-3">
                        {pillar.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2.5">
                            <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-900 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </TiltCard>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 4: WHERE DDARP SITS IN THE STACK
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/50 text-emerald-600 text-sm font-medium mb-4">
                <ShieldCheck className="w-4 h-4" />
                Operational Neutrality
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Where DDARP Sits in the Stack
              </h2>
              <p className="text-gray-900 max-w-2xl mx-auto">
                DDARP is an observation layer. It complements existing network
                telemetry. It never modifies BGP, never routes traffic, and
                never operates a control plane.
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-4">
            {stackLayers.map((layer, index) => (
              <StackRow key={layer.role} layer={layer} index={index} />
            ))}
          </div>

          <ScrollReveal delay={0.4}>
            <p className="text-center text-sm text-slate-500 mt-8 max-w-3xl mx-auto">
              Operational neutrality is formalized in Patent Claim 11 of the
              91-claim USPTO provisional application. The system exposes
              continuity intelligence through publication interfaces only, and
              does not modify external routing control semantics or forwarding
              behavior in networks under observation.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 5: FINAL CTA
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        {/* Subtle radial glow */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(15,108,189,0.04) 0%, transparent 60%)',
          }}
        />

        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="text-center">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                Built on a proven 118-node multi-cloud substrate.
              </h2>
              <p className="text-gray-900 text-lg mb-6 max-w-2xl mx-auto">
                {stats?.nodes ?? 118} observation nodes are live across three
                hyperscale clouds today. The continuity substrate is
                operational; the publication API and 65-node anchor spine
                are roadmap. The IP is filed.
              </p>

              {/* Cloud provider badges */}
              <div className="flex items-center justify-center gap-3 mb-10 flex-wrap">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-900 text-xs font-medium shadow-sm">
                  <AwsLogo height={14} /> Amazon Web Services
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-900 text-xs font-medium shadow-sm">
                  <GcpLogo height={14} /> Google Cloud
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-900 text-xs font-medium shadow-sm">
                  <AzureLogo height={14} /> Microsoft Azure
                </span>
              </div>

              <PublicCTAPair />

            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
